import { useEffect, useMemo, useState } from 'react';
import type { ComponentType } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import type { Entity } from '@backstage/catalog-model';
import {
  Content,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Tooltip, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import AssignmentIcon from '@material-ui/icons/Assignment';
import CreateNewFolderIcon from '@material-ui/icons/CreateNewFolder';
import GroupIcon from '@material-ui/icons/Group';
import LibraryAddIcon from '@material-ui/icons/LibraryAdd';
import { brand, howden } from '../../themes/howdenTheme';
import { AVAILABILITY_LABEL } from '../../modules/capabilities/capabilities';
import type { DataSphereCapability } from '../../modules/capabilities/capabilities';
import { groupServices } from './serviceGroups';

// Icons are decorative: every card also has a title and a description.
const SERVICE_ICONS: Record<string, ComponentType> = {
  'howden-databricks-spoke-request': CreateNewFolderIcon,
  'howden-databricks-catalog-schema-request': LibraryAddIcon,
  'howden-user-groups-request': GroupIcon,
};

const useStyles = makeStyles(theme => ({
  root: {
    maxWidth: 1680,
    margin: '0 auto',
  },
  pageTitle: {
    color: brand.primary,
  },
  pageRule: {
    width: theme.spacing(7),
    height: 4,
    border: 0,
    margin: theme.spacing(2, 0, 3),
    backgroundColor: howden.gold,
  },
  intro: {
    color: howden.grey,
    maxWidth: '72ch',
    marginBottom: theme.spacing(5),
  },
  group: {
    marginBottom: theme.spacing(6),
  },
  groupTitle: {
    color: brand.primary,
  },
  // Brand motif: a 4px gold rule.
  groupRule: {
    width: theme.spacing(6),
    height: 4,
    border: 0,
    margin: theme.spacing(1.5, 0, 2),
    backgroundColor: howden.gold,
  },
  groupDescription: {
    color: howden.grey,
    marginBottom: theme.spacing(3),
  },
  list: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 320px), 1fr))',
    gap: theme.spacing(3),
  },
  card: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: theme.spacing(3),
    backgroundColor: howden.white,
    border: `1px solid ${howden.grey10}`,
    borderRadius: 12,
    transition: 'box-shadow 150ms ease, border-color 150ms ease',
    '&:hover': {
      borderColor: brand.primary,
      boxShadow: '0 8px 24px rgba(23, 63, 53, 0.12)',
    },
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
    },
  },
  iconBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 48,
    height: 48,
    marginBottom: theme.spacing(2),
    borderRadius: 12,
    backgroundColor: brand.accent,
    color: brand.onAccent,
  },
  cardTitle: {
    color: brand.primary,
    marginBottom: theme.spacing(1),
  },
  // The link covers the whole card, so the card is one click and one tab stop.
  // The focus ring sits on that cover so it outlines the card, not the title.
  cardLink: {
    color: 'inherit',
    textDecoration: 'none',
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      borderRadius: 12,
    },
    // Doubled class to beat the global `body.theme-howden :focus-visible` ring.
    '&&:focus-visible': {
      outline: 'none',
    },
    '&:focus-visible::after': {
      outline: `3px solid ${brand.primary}`,
      outlineOffset: 2,
    },
  },
  description: {
    color: howden.grey,
    flexGrow: 1,
  },
  action: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(3),
    color: brand.primary,
    fontWeight: 600,
  },
  tooltip: {
    backgroundColor: brand.primary,
    color: howden.white,
    fontSize: 14,
    lineHeight: 1.5,
    padding: theme.spacing(1, 1.5),
    maxWidth: 320,
  },
  empty: {
    color: howden.grey,
  },
  // Planned roadmap services: same card shape, no link, dashed border so they
  // never read as something you can start.
  plannedCard: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: theme.spacing(3),
    backgroundColor: howden.grey5,
    border: `1px dashed ${howden.grey60}`,
    borderRadius: 12,
  },
  plannedInteractive: {
    position: 'relative',
    transition: 'border-color 150ms ease, background-color 150ms ease',
    '&:hover': {
      borderColor: brand.primary,
      backgroundColor: howden.white,
    },
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
    },
  },
  plannedTitle: {
    color: howden.grey,
    marginBottom: theme.spacing(1),
  },
  plannedMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    marginTop: theme.spacing(3),
    fontSize: 14,
    color: '#5c5c5c',
  },
  plannedChip: {
    padding: theme.spacing(0.25, 1),
    borderRadius: 2,
    backgroundColor: howden.white,
    border: `1px solid ${brand.primary}`,
    color: brand.primary,
    fontWeight: 600,
  },
}));

const PlannedCard = ({ capability }: { capability: DataSphereCapability }) => {
  const classes = useStyles();
  const descriptionId = `planned-${capability.id}-description`;
  return (
    <li>
      <div className={`${classes.plannedCard} ${classes.plannedInteractive}`}>
        <Typography
          variant="h5"
          component="h3"
          className={classes.plannedTitle}
        >
          <RouterLink
            to={`planned/${capability.id}`}
            className={classes.cardLink}
            aria-describedby={descriptionId}
          >
            {capability.title}
          </RouterLink>
        </Typography>
        <Typography
          id={descriptionId}
          variant="body1"
          className={classes.description}
        >
          {capability.description}
        </Typography>
        <div className={classes.plannedMeta}>
          <span className={classes.plannedChip}>
            {AVAILABILITY_LABEL[capability.availability]}
          </span>
          <span className={classes.action} aria-hidden="true">
            Find out more
            <ArrowForwardIcon fontSize="small" />
          </span>
        </div>
      </div>
    </li>
  );
};

const ServiceCard = ({ service }: { service: Entity }) => {
  const classes = useStyles();
  const { name, namespace = 'default', title, description } = service.metadata;
  const label = title ?? name;
  const descriptionId = `service-${name}-description`;
  const Icon = SERVICE_ICONS[name] ?? AssignmentIcon;

  return (
    <li>
      <div className={classes.card}>
        <span className={classes.iconBadge} aria-hidden="true">
          <Icon />
        </span>
        <Typography variant="h5" component="h3" className={classes.cardTitle}>
          <Tooltip
            title={`Open the ${label} form`}
            placement="top-start"
            classes={{ tooltip: classes.tooltip }}
          >
            <RouterLink
              to={`${namespace}/${name}`}
              className={classes.cardLink}
              aria-describedby={description ? descriptionId : undefined}
            >
              {label}
            </RouterLink>
          </Tooltip>
        </Typography>
        {description ? (
          <Typography
            id={descriptionId}
            variant="body1"
            className={classes.description}
          >
            {description}
          </Typography>
        ) : null}
        <span className={classes.action} aria-hidden="true">
          Start
          <ArrowForwardIcon fontSize="small" />
        </span>
      </div>
    </li>
  );
};

/**
 * Lists the workflow templates the signed-in user may run, grouped by what
 * they do. Replaces the stock scaffolder list, which has a search and filter
 * column that reads as a second left hand menu.
 */
export const ServicesPage = () => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  // Optional chaining: unit tests mock useApi and return no config API.
  const showPlanned =
    useApi(configApiRef)?.getOptionalBoolean(
      'datasphere.showPlannedServices',
    ) ?? false;
  const [templates, setTemplates] = useState<Entity[]>();
  const [error, setError] = useState<Error>();

  // The backend permission policy only returns templates the persona can run.
  useEffect(() => {
    let cancelled = false;
    catalogApi
      .getEntities({
        filter: { kind: 'Template' },
        fields: [
          'kind',
          'metadata.name',
          'metadata.namespace',
          'metadata.title',
          'metadata.description',
          'metadata.annotations',
        ],
      })
      .then(response => {
        if (!cancelled) setTemplates(response.items);
      })
      .catch(e => {
        if (!cancelled) setError(e);
      });
    return () => {
      cancelled = true;
    };
  }, [catalogApi]);

  // Side menu entries link to /create#services-<area>. The cards load after
  // the first render, so scroll once they are on the page.
  const { hash } = useLocation();
  useEffect(() => {
    if (!templates || !hash) return;
    document
      .getElementById(decodeURIComponent(hash.slice(1)))
      ?.scrollIntoView({ block: 'start' });
  }, [templates, hash]);

  const groups = useMemo(
    () => groupServices(templates ?? [], { includePlanned: showPlanned }),
    [templates, showPlanned],
  );

  if (error) {
    return (
      <Content>
        <ResponseErrorPanel error={error} />
      </Content>
    );
  }
  if (!templates) {
    return (
      <Content>
        <Progress />
      </Content>
    );
  }

  return (
    <Content>
      <div className={classes.root}>
        <Typography variant="h2" component="h1" className={classes.pageTitle}>
          Services
        </Typography>
        <hr className={classes.pageRule} aria-hidden="true" />
        <Typography variant="body1" className={classes.intro}>
          Choose the service you need. Each one opens a short guided form.
          {showPlanned
            ? ' Services with a dashed outline are on the platform roadmap. Open one to see when it is planned and what to do in the meantime.'
            : null}
        </Typography>
        {groups.length === 0 ? (
          <Typography variant="body1" className={classes.empty}>
            There are no services available to you yet. Ask a DataSphere
            administrator if you need access.
          </Typography>
        ) : null}
        {groups.map(({ group, services, planned }) => {
          const headingId = `service-group-${group.id}`;
          return (
            <section
              key={group.id}
              id={`services-${group.id}`}
              aria-labelledby={headingId}
              className={classes.group}
            >
              <Typography
                id={headingId}
                variant="h4"
                component="h2"
                className={classes.groupTitle}
              >
                {group.title}
              </Typography>
              <hr className={classes.groupRule} aria-hidden="true" />
              <Typography variant="body1" className={classes.groupDescription}>
                {group.description}
              </Typography>
              <ul className={classes.list}>
                {services.map(service => (
                  <ServiceCard key={service.metadata.name} service={service} />
                ))}
                {planned.map(capability => (
                  <PlannedCard key={capability.id} capability={capability} />
                ))}
              </ul>
            </section>
          );
        })}
      </div>
    </Content>
  );
};
